import { useModel as _useModel, mergeModels as _mergeModels, defineComponent as _defineComponent } from 'vue';
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, withCtx as _withCtx, createBlock as _createBlock } from "vue";
const _hoisted_1 = ["id"];
const _hoisted_2 = { class: "text-sm text-(--colors-text-body-subtle) mb-4" };
const _hoisted_3 = { class: "space-y-4" };
const _hoisted_4 = {
    key: 0,
    role: "alert",
    class: "text-sm text-(--colors-text-error)",
    "data-testid": "collecte-form-rename-error"
};
import { ref, useId, watch } from "vue";
import { Modal, TextInput, Button } from "@eudonet/opal";
import { useDonationFormStore } from "../../stores/useDonationFormStore";
import { resolveApiErrorMessage } from "@/infrastructure/services/ApiErrorService";
import { useAppToast } from "@/shared/composables/useAppToast";
// Backend errorCode -> i18n key for the rename failures the manager can act on (R70). The two 422
// title codes are also pre-validated below, and stay mapped so a command-validator 422 still renders
// an actionable message. 409 Form.NotEditable is reachable even though the list omits the action on
// an Archived row: the loaded page can be stale (another admin archived the form meanwhile).
// Form.NotFound covers the form being deleted between load and submit. Unknown codes and network
// errors fall back to saveFailed. The codes are the wire contract (DonationFormErrorCodes).
const TITLE_MAX_LENGTH = 255;
/**
 * Counts CODE POINTS, like every bound this guard mirrors: the domain's `DonationForm::rename` trims
 * and then checks `mb_strlen`, the command validator checks `mb_strlen(trim(...))`, and Postgres
 * `VARCHAR(255)` counts characters. `String.length` counts UTF-16 code units instead, so it
 * double-counts every astral character — 230 letters + 20 emoji is `.length === 270` but only 250
 * characters — and would refuse a title the backend stores happily, with a message that is factually
 * false. It only ever OVER-counts, so this is a false refusal, never a bypass.
 */
export default /*@__PURE__*/ _defineComponent({
    __name: 'FormRenameModal',
    props: /*@__PURE__*/ _mergeModels({
        formId: {},
        currentTitle: {}
    }, {
        "modelValue": { type: Boolean, ...{ required: true } },
        "modelModifiers": {},
    }),
    emits: ["update:modelValue"],
    setup(__props) {
        const RENAME_ERROR_KEYS = {
            "Form.NotEditable": "collecte.list.rename.errors.notEditable",
            "Form.NotFound": "collecte.list.rename.errors.notFound",
            "Form.Title.Required": "collecte.list.rename.errors.titleRequired",
            "Form.Title.TooLong": "collecte.list.rename.errors.titleTooLong",
        };
        const props = __props;
        const open = _useModel(__props, "modelValue");
        // No emit: the store patches the list row from the response, so the parent has nothing to do.
        const store = useDonationFormStore();
        const { toastSuccess } = useAppToast();
        // Names the dialog for assistive tech (`aria-labelledby` -> the `<h2>` above).
        const titleId = useId();
        const title = ref("");
        const titleError = ref(null);
        const submitError = ref(null);
        /** Mirrors the backend bound (DonationForm title max 255), measured on the TRIMMED value like it. */
        function countCharacters(value) {
            return [...value].length;
        }
        // Re-seed from the CURRENT row on every open so a previous edit (or a previously chosen row) never
        // leaks into the next rename, and clear both error surfaces. The modal stays mounted in the list
        // view and its props change under it, exactly like FormDuplicateModal.
        watch(open, (isOpen) => {
            if (isOpen) {
                title.value = props.currentTitle;
                titleError.value = null;
                submitError.value = null;
            }
        });
        function validate() {
            titleError.value = null;
            const trimmedTitle = title.value.trim();
            if (!trimmedTitle) {
                titleError.value = "collecte.list.rename.errors.titleRequired";
            }
            else if (countCharacters(trimmedTitle) > TITLE_MAX_LENGTH) {
                titleError.value = "collecte.list.rename.errors.titleTooLong";
            }
            return !titleError.value;
        }
        async function submit() {
            submitError.value = null;
            if (!validate()) {
                return;
            }
            try {
                await store.renameForm(props.formId, title.value.trim());
                open.value = false;
                toastSuccess("collecte.list.rename.success");
            }
            catch (e) {
                // Map the backend errorCode to an actionable, localized message (R70). resolveApiErrorMessage
                // returns ALREADY-TRANSLATED text — render it verbatim, never through $t() again (R86: a literal
                // @ / { / | in the copy would then blow up the message compiler). It falls back to saveFailed for
                // unknown codes and network errors (R46 — user feedback here, no logging).
                submitError.value = resolveApiErrorMessage(e, RENAME_ERROR_KEYS, "collecte.list.rename.errors.saveFailed");
            }
        }
        return (_ctx, _cache) => {
            return (_openBlock(), _createBlock(_unref(Modal), {
                modelValue: open.value,
                "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event) => ((open).value = $event)),
                size: "md",
                "aria-labelledby": _unref(titleId)
            }, {
                header: _withCtx(() => [
                    _createElementVNode("h2", {
                        id: _unref(titleId),
                        class: "text-xl font-bold text-(--colors-text-heading)",
                        "data-testid": "collecte-form-rename-heading"
                    }, _toDisplayString(_ctx.$t("collecte.list.rename.title")), 9 /* TEXT, PROPS */, _hoisted_1)
                ]),
                footer: _withCtx(() => [
                    _createVNode(_unref(Button), {
                        variant: "secondary",
                        label: _ctx.$t('common.cancel'),
                        "data-testid": "collecte-form-rename-cancel",
                        onClick: _cache[1] || (_cache[1] = ($event) => (open.value = false))
                    }, null, 8 /* PROPS */, ["label"]),
                    _createVNode(_unref(Button), {
                        variant: "primary",
                        label: _ctx.$t('collecte.list.rename.submit'),
                        disabled: _unref(store).isSaving,
                        "data-testid": "collecte-form-rename-submit",
                        onClick: submit
                    }, null, 8 /* PROPS */, ["label", "disabled"])
                ]),
                default: _withCtx(() => [
                    _createElementVNode("p", _hoisted_2, _toDisplayString(_ctx.$t("collecte.list.rename.subtitle")), 1 /* TEXT */),
                    _createElementVNode("div", _hoisted_3, [
                        _createVNode(_unref(TextInput), {
                            modelValue: title.value,
                            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event) => ((title).value = $event)),
                            label: _ctx.$t('collecte.list.rename.titleLabel'),
                            placeholder: _ctx.$t('collecte.list.rename.titlePlaceholder'),
                            error: titleError.value ? _ctx.$t(titleError.value) : undefined,
                            required: "",
                            "full-width": "",
                            "data-testid": "collecte-form-rename-title"
                        }, null, 8 /* PROPS */, ["modelValue", "label", "placeholder", "error"]),
                        (submitError.value)
                            ? (_openBlock(), _createElementBlock("p", _hoisted_4, _toDisplayString(submitError.value), 1 /* TEXT */))
                            : _createCommentVNode("v-if", true)
                    ])
                ]),
                _: 1 /* STABLE */
            }, 8 /* PROPS */, ["modelValue", "aria-labelledby"]));
        };
    }
});
